import { Disclosure, DisclosureButton } from "@headlessui/react";
import { Avatar, Card, Icon, IconButton, Tooltip, Typography } from "@/components/atoms";
import { getManageClientPath } from "@/components/organisms/Clients/helpers";
import { useOperator } from "@/hooks";
import { usePermissions } from "@/permissions";
import { EPermission } from "@/permissions/types";
import { useAppDispatch } from "@/redux/hooks";
import { setAction } from "@/redux/slices/booking/bookingSlice";
import { BookingDetails } from "@/redux/slices/booking/types";
import { clsx, getAssetUrl } from "@/utils";
import { DetailField } from "../../../common/DetailField";

interface PassengerSectionProps {
  booking: BookingDetails;
}

export const PassengerSection = ({ booking }: PassengerSectionProps) => {
  const { hasPermission } = usePermissions();
  const dispatch = useAppDispatch();
  const { id: operatorId } = useOperator();
  const { passenger, account, stops, referenceNumber, referenceExternal, metadata } = booking;
  const isReassignEnabled = metadata.isReassignClientAllowed;
  const isAcceptedFromNetwork = metadata.isAcceptedFromNetwork;
  const canReassignClient = hasPermission(EPermission.BookingReassignClientManage);

  const passengerName = passenger?.name ?? "-";
  const passengerPhone = passenger?.phone ? `(${passenger.phone})` : "";
  const passengerLine = [passengerName, passengerPhone].filter(Boolean).join(" ");
  const paxCount = typeof passenger?.count === "number" ? passenger.count : "-";
  const companyName = account?.company?.name ?? undefined;
  const clientID = account?.id ?? undefined;
  const clientName = account?.name ?? undefined;
  const clientAvatar = account?.avatar ? getAssetUrl(account.avatar, { height: 24, width: 24 }) : undefined;
  // DEV-1661: the operator's own code for this client, shown under the name so staff who work by
  // their codes (Royale's read like "SYD2297 Black Communications") can recognise the client at a
  // glance. Every provider has this; clients without a code simply show the name alone.
  const clientIdentifier = account?.identifier || undefined;
  const luggage = stops?.[0]?.flight?.luggage;
  const preferences = passenger?.preferences;
  const passengerNotes = preferences?.driverNotes;

  const rows: Array<React.ReactNode> = [];

  rows.push(
    <DetailField
      key="passenger"
      leading={<Icon name="User" size="lg" className="text-neutral-dark-gray" />}
      label="Passenger"
      value={passengerLine || "-"}
    />
  );

  if (!isAcceptedFromNetwork) {
    if (clientName) {
      rows.push(
        <DetailField
          key="client"
          leading={
            clientAvatar ? (
              <Avatar src={clientAvatar} alt={clientName} size="xs" />
            ) : (
              <Icon name="User" size="lg" className="text-neutral-dark-gray" />
            )
          }
          label="Client"
          value={
            clientIdentifier ? (
              <span className="flex flex-col">
                <Typography variant="paragraph">{clientName}</Typography>
                <Typography variant="small" className="text-neutral-dark-gray">
                  {clientIdentifier}
                </Typography>
              </span>
            ) : (
              clientName
            )
          }
          right={
            <div className="flex gap-1">
              {isReassignEnabled && canReassignClient && (
                <IconButton
                  iconName="ArrowSwapHorizontal"
                  iconSize="md"
                  size="xs"
                  variant="primary"
                  className="h-8 w-8"
                  onClick={() => dispatch(setAction("reassign-client"))}
                />
              )}
              <Tooltip content="View Client Profile" placement="top">
                <IconButton
                  iconName="User"
                  iconSize="md"
                  size="xs"
                  variant="primary"
                  className="h-8 w-8"
                  /*
                   * The in-app Manage Client record, not the legacy context-switch. That old path
                   * bounced through PHP and — since the client portal cutover redirected those
                   * routes — landed the operator back on the bookings screen instead of the client.
                   *
                   * "noopener" is load-bearing (see BookingActionsBar): with a same-origin opener,
                   * kinde-auth-react treats the new tab as its own login popup — it postMessages a
                   * KINDE_AUTH_RESULT to the opener and window.close()s the tab as it boots. Anchors
                   * get this implicitly from the browser; window.open does not.
                   */
                  onClick={
                    clientID
                      ? () =>
                          window.open(
                            `${window.location.origin}/dashboard${getManageClientPath(operatorId, clientID)}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                      : undefined
                  }
                />
              </Tooltip>
            </div>
          }
        />
      );
    }
  }

  rows.push(
    <DetailField key="pax" leading={<Icon name="People" size="lg" className="text-neutral-dark-gray" />} label="Pax" value={paxCount} />
  );

  if (!isAcceptedFromNetwork) {
    if (companyName) {
      rows.push(
        <DetailField
          key="company"
          leading={<Icon name="Buildings" size="lg" className="text-neutral-dark-gray" />}
          label="Company"
          value={companyName}
        />
      );
    }

    if (luggage) {
      rows.push(
        <DetailField
          key="luggage"
          leading={<Icon name="iconsax-luggage" isCustom size="lg" className="text-neutral-dark-gray" />}
          label="Luggage"
          value={
            <Typography variant="paragraph" className="capitalize">
              {luggage}
            </Typography>
          }
        />
      );
    }

    if (referenceNumber) {
      rows.push(
        <DetailField
          key="booking-ref"
          leading={<Icon name="NoteText" size="lg" className="text-neutral-dark-gray" />}
          label="Booking Reference"
          value={referenceNumber}
        />
      );
    }

    if (referenceExternal) {
      rows.push(
        <DetailField
          key="external-ref"
          leading={<Icon name="NoteText" size="lg" className="text-neutral-dark-gray" />}
          label="External Reference"
          value={referenceExternal}
        />
      );
    }

    if (preferences?.seating) {
      rows.push(
        <DetailField
          key="seating"
          leading={<Icon name="iconsax-chair" isCustom size="lg" className="text-neutral-dark-gray" />}
          label="Seating Preference"
          value={preferences.seating.replace("seat", "")}
        />
      );
    }

    if ((preferences?.radioStation && preferences.radioStation.length > 0) || (preferences?.radio && preferences.radio.length > 0)) {
      rows.push(
        <DetailField
          key="radio"
          leading={<Icon name="Radio" size="lg" className="text-neutral-dark-gray" />}
          label="Radio Preference"
          value={preferences?.radioStation ?? preferences?.radio ?? "-"}
        />
      );
    }

    if (preferences?.quietRide) {
      rows.push(
        <DetailField
          key="quiet-ride"
          leading={<Icon name="VolumeLow1" size="lg" className="text-neutral-dark-gray" />}
          label="Quiet Ride"
          value={preferences.quietRide}
        />
      );
    }

    if (preferences?.vehicleTemperature) {
      rows.push(
        <DetailField
          key="vehicle-temp"
          leading={<Icon name="iconsax-temperature" isCustom size="lg" className="text-neutral-dark-gray" />}
          label="Vehicle Temp"
          value={`${preferences.vehicleTemperature}° Celsius`}
        />
      );
    }
  }

  if (passengerNotes && passengerNotes.length > 0) {
    rows.push(
      <DetailField
        key="passenger-notes"
        leading={<Icon name="NoteText" size="lg" className="text-neutral-dark-gray" />}
        label="Passenger Notes"
        value={passengerNotes}
      />
    );
  }

  const firstRows = rows.slice(0, 4);
  const restRows = rows.slice(4);

  return (
    <Card className="space-y-3 p-2.5">
      {firstRows}
      {rows.length > 4 && (
        <Disclosure as="div">
          {({ open }) => (
            <>
              <div
                className={clsx(
                  "grid transition-[grid-template-rows] duration-200 ease-in-out",
                  open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                )}
              >
                <div className="space-y-3 overflow-hidden">{restRows}</div>
              </div>
              <div className="flex items-center justify-center pt-1">
                <DisclosureButton
                  aria-label={open ? "Collapse passenger details" : "Expand passenger details"}
                  className="inline-flex w-full items-center justify-center rounded-md p-0 hover:bg-neutral-gray"
                >
                  <Icon name={open ? "ArrowUp2" : "ArrowDown2"} className="text-neutral-dark-gray" />
                </DisclosureButton>
              </div>
            </>
          )}
        </Disclosure>
      )}
    </Card>
  );
};
